import { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SheetsContext = createContext(null);

const SHEET_KEYS = ['messages', 'notifications', 'saved'];

function clearSheets(params) {
  SHEET_KEYS.forEach((key) => params.delete(key));
}

export function SheetsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const messagesOpen = params.has('messages');
  const threadId = messagesOpen ? params.get('messages') || null : null;
  const notificationsOpen = params.has('notifications');
  const savedOpen = params.has('saved');

  const patchSearch = useCallback(
    (mutator, { replace = false } = {}) => {
      const next = new URLSearchParams(location.search);
      mutator(next);
      const qs = next.toString();
      navigate(
        {
          pathname: location.pathname,
          search: qs ? `?${qs}` : '',
          hash: location.hash,
        },
        { replace },
      );
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  const openMessages = useCallback(
    (id = null) => {
      patchSearch((p) => {
        clearSheets(p);
        p.set('messages', id || '');
      });
    },
    [patchSearch],
  );

  const closeMessages = useCallback(() => {
    patchSearch((p) => {
      p.delete('messages');
    }, { replace: true });
  }, [patchSearch]);

  const setThreadId = useCallback(
    (id) => {
      patchSearch((p) => {
        clearSheets(p);
        p.set('messages', id || '');
      });
    },
    [patchSearch],
  );

  const openNotifications = useCallback(() => {
    patchSearch((p) => {
      clearSheets(p);
      p.set('notifications', '');
    });
  }, [patchSearch]);

  const closeNotifications = useCallback(() => {
    patchSearch((p) => {
      p.delete('notifications');
    }, { replace: true });
  }, [patchSearch]);

  const openSaved = useCallback(() => {
    patchSearch((p) => {
      clearSheets(p);
      p.set('saved', '');
    });
  }, [patchSearch]);

  const closeSaved = useCallback(() => {
    patchSearch((p) => {
      p.delete('saved');
    }, { replace: true });
  }, [patchSearch]);

  const value = useMemo(
    () => ({
      messagesOpen,
      threadId,
      setThreadId,
      openMessages,
      closeMessages,
      notificationsOpen,
      openNotifications,
      closeNotifications,
      savedOpen,
      openSaved,
      closeSaved,
    }),
    [
      messagesOpen,
      threadId,
      setThreadId,
      openMessages,
      closeMessages,
      notificationsOpen,
      openNotifications,
      closeNotifications,
      savedOpen,
      openSaved,
      closeSaved,
    ],
  );

  return <SheetsContext.Provider value={value}>{children}</SheetsContext.Provider>;
}

/** Redirect legacy paths into query-param sheets on the current (or home) URL. */
export function sheetSearchFromLegacy(pathname) {
  if (pathname === '/messages' || pathname === '/messages/') return '?messages=';
  const messageMatch = pathname.match(/^\/messages\/([^/]+)\/?$/);
  if (messageMatch) return `?messages=${encodeURIComponent(messageMatch[1])}`;
  if (pathname === '/notifications' || pathname === '/notifications/') return '?notifications=';
  if (pathname === '/saved' || pathname === '/saved/') return '?saved=';
  return null;
}

export function useSheets() {
  const ctx = useContext(SheetsContext);
  if (!ctx) throw new Error('useSheets must be used within SheetsProvider');
  return ctx;
}
